'use client';

import dynamic from 'next/dynamic';
import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';

import eventNames from '@/constants/eventNames';
import FinishModal from '@/containers/InboundStone/components/InboundFormV2/components/FinishModal';
import { useInbound } from '@/contexts/inbound';
import { loadTwilio } from '@/scripts/twillio';
import { normalizedPath } from '@/services/normalizedPath';

import StyledComponent from '../StyledComponent';
import {
  chatIsOpenState,
  finishMessagesState,
  widgetActiveChatIsOpenState,
  widgetIsHiddenState,
  widgetIsOpenState,
} from './atoms';
import { isWhatsappButton } from './utils/personalizations';

const Card = dynamic(() => import('./components/Card'));

const ActiveCardContent = dynamic(() => import('./components/ActiveCard'), {
  ssr: false,
});
const allowListActiveChat = ['/', '/maquina-de-cartao'];
const denyListActiveChat = [
  '/adquira',
  '/planos',
  '/planos-mgm',
  '/produtos-stone',
  '/restaurante',
  '/masterchefbr',
  '/solucao-completa',
];

const Widget = ({
  twilioReqSource,
}: {
  twilioReqSource: 'stone_widget_active_chat' | 'stone_widget_active_chat_home';
}) => {
  const router = useRouter();
  const pathname = usePathname() || '/';
  const [hasClosedActiveChat, setHasClosedActiveChat] =
    useState<boolean>(false);
  const [widgetIsHidden, setWidgetIsHidden] =
    useRecoilState(widgetIsHiddenState);
  const [widgetIsOpen, setWidgetIsOpenState] =
    useRecoilState(widgetIsOpenState);
  const [widgetActiveChatIsOpen] = useRecoilState(widgetActiveChatIsOpenState);
  const [chatIsOpen, setChatIsOpen] = useRecoilState(chatIsOpenState);
  const { channels } = useInbound();
  const [showFinishModal, setShowFinishModal] =
    useRecoilState(finishMessagesState);
  const checkTwilioStarter = useCallback(() => {
    const twilioFlexCf = localStorage.getItem('twilio-flex-cf');
    const twilioIsClient = localStorage.getItem('twilio-is-client');
    const parsedTwilioFlexCf = twilioFlexCf ? JSON.parse(twilioFlexCf) : null;

    if (
      parsedTwilioFlexCf?.flex?.session?.engagementStage === 'CF_IN_ENGAGEMENT'
    ) {
      loadTwilio({ loadRcFlow: Boolean(twilioIsClient) });
      return true;
    }

    return false;
  }, []);

  // UseEffect para validar se o Widget será aberto.
  useEffect(() => {
    addEventListener(eventNames.toggleVisibility, () => {
      setWidgetIsHidden(value => !value);
    });

    addEventListener(eventNames.setWidgetVisible, () => {
      setWidgetIsHidden(false);
    });

    addEventListener(eventNames.openWidget, () => {
      setWidgetIsHidden(false);
      setWidgetIsOpenState(true);

      setChatIsOpen(true);
      sessionStorage.setItem('alreadyStartedChatState', 'true');
    });

    addEventListener(eventNames.closeWidget, () => {
      setWidgetIsHidden(true);
      widgetIsOpen && setWidgetIsOpenState(false);
    });

    const hasStartedTwilio = checkTwilioStarter();

    if (hasStartedTwilio) {
      if (!widgetIsHidden) {
        setWidgetIsHidden(true);
        widgetIsOpen && setWidgetIsOpenState(false);
      }
    }

    if (!hasStartedTwilio && widgetIsHidden) {
      setWidgetIsHidden(false);
    }

    const currentPath = normalizedPath(pathname);
    if (denyListActiveChat.includes(currentPath)) {
      setWidgetIsHidden(true);
      widgetIsOpen && setWidgetIsOpenState(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkTwilioStarter, pathname, setWidgetIsOpenState, widgetIsOpen]);

  useEffect(() => {
    setHasClosedActiveChat(
      localStorage.getItem('hasClosedActiveChat') === 'true',
    );
  }, []);

  useEffect(() => {
    setChatIsOpen(sessionStorage.getItem('alreadyStartedChatState') === 'true');
  }, [setChatIsOpen]);

  const chatOrWhatsappVariant = isWhatsappButton(pathname);

  const ActiveChat = useCallback((): JSX.Element => {
    if (
      !hasClosedActiveChat &&
      chatOrWhatsappVariant !== 1 &&
      widgetActiveChatIsOpen &&
      !widgetIsOpen &&
      allowListActiveChat.includes(pathname) &&
      channels.length > 0 &&
      !chatIsOpen
    ) {
      if (widgetIsOpen) setWidgetIsOpenState(false);

      return <ActiveCardContent twilioReqSource={twilioReqSource} />;
    }
    return <></>;
  }, [
    channels.length,
    chatOrWhatsappVariant,
    hasClosedActiveChat,
    widgetActiveChatIsOpen,
    widgetIsOpen,
    chatIsOpen,
    pathname,
    setWidgetIsOpenState,
    twilioReqSource,
  ]);

  return (
    <>
      <FinishModal
        channel="whatsapp"
        show={showFinishModal}
        onBackToSite={() => {
          setShowFinishModal(false);
          setChatIsOpen(false);
          router.push('/');
        }}
      />
      <StyledComponent>
        <Card />
        <ActiveChat />
      </StyledComponent>
    </>
  );
};

export default Widget;
